import { useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { Wallet, ArrowRight, Loader2 } from 'lucide-react';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const { requestOtp, verifyOtp, isLoading, error, clearError } = useAuthStore();

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await requestOtp(email);
      setStep('code');
    } catch {
      // Error handled by store
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await verifyOtp(email, code);
    } catch {
      // Error handled by store
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary/20 via-background to-background p-12 flex-col justify-between">
        <div className="flex items-center">
          <Wallet className="h-10 w-10 text-primary" />
          <span className="ml-3 text-2xl font-bold gradient-text">TaxVault</span>
        </div>
        <div>
          <h1 className="text-4xl font-bold mb-4">
            Take control of your taxes
          </h1>
          <p className="text-lg text-muted-foreground max-w-md">
            Track multiple businesses, crypto investments, and maximize your deductions with AI-powered insights.
          </p>
        </div>
        <div className="text-sm text-muted-foreground">
          Secure. Private. Your data stays yours.
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center mb-8">
            <Wallet className="h-10 w-10 text-primary" />
            <span className="ml-3 text-2xl font-bold gradient-text">TaxVault</span>
          </div>

          <div className="bg-card border border-border rounded-2xl p-8 shadow-xl">
            <h2 className="text-2xl font-semibold mb-2">
              {step === 'email' ? 'Sign in' : 'Enter code'}
            </h2>
            <p className="text-muted-foreground mb-6">
              {step === 'email'
                ? "Enter your email to receive a one-time code"
                : `We sent a code to ${email}`}
            </p>

            {error && (
              <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
                {error}
              </div>
            )}

            {step === 'email' ? (
              <form onSubmit={handleEmailSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="you@example.com"
                    required
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 rounded-lg font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50"
                >
                  {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      Continue <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleCodeSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-center text-2xl tracking-widest"
                    placeholder="000000"
                    maxLength={6}
                    required
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  disabled={isLoading || code.length !== 6}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 rounded-lg font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50"
                >
                  {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      Sign In <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStep('email');
                    setCode('');
                    clearError();
                  }}
                  className="w-full mt-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-smooth"
                >
                  Use a different email
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
